import React, { ButtonHTMLAttributes, forwardRef } from 'react';
import { IconProps } from '../../assets/images/svg';
import { cn } from '../../helpers';
import styles from './iconbutton.module.scss';

export interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
    /**
     * The aria label is mandatory otherwise screen readers cannot assist
     */
    'aria-label': string;

    /**
     * Make the icon button completely round
     */
    isRound?: boolean;

    /**
     * The icon used inside the button
     */
    icon: React.ReactElement<IconProps>;

    /**
     * Determine the dimensions of the icon button
     */
    size?: 'small' | 'medium';

    /**
     * Determine the variant of the iconbutton
     */
    variant?: 'ghost' | 'solid';
}

/**
 * IconButton
 */
export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
    ({ size = 'medium', isRound, variant = 'solid', ...props }, ref) => {
        const { icon, children, className } = props;

        const classNames = cn(styles.iconButton, {
            [className]: Boolean(className),
            [styles.isRound]: isRound,
            [styles.solid]: variant === 'solid',
            [styles.ghost]: variant === 'ghost',
            [styles.small]: size === 'small',
            [styles.medium]: size === 'medium',
        });

        // The icon is available both as a children and icon
        const element = icon || children;

        const validElement = React.isValidElement(element)
            ? React.cloneElement<ButtonHTMLAttributes<HTMLElement>>(element, {
                  'aria-hidden': true,
              })
            : null;

        return (
            <span className={classNames} ref={ref} {...props} tabIndex={0} role="button">
                {validElement}
            </span>
        );
    }
);

IconButton.displayName = 'IconButton';
